/**
 * Which routes belong to a competition, a group, or a scorecard — and what a
 * route is worth right now.
 *
 * Route sets live on `settings` in two shapes: `groupRouteIds` per group, and
 * a `routeIds` union kept for older competitions and the admin map filter.
 * Everything here reads through that pair rather than picking one.
 */
import { filterPaidCompetitors, getGroupRouteCompPoints } from "@sendly/shared";
import { getFinalsGroupsInfo } from "./finals";
import { sortGroupsForDisplay } from "./groups";

/**
 * Resolve routeIds for a specific group.
 * If the competition has per-group route assignments configured (`groupRouteIds`
 * contains any entry), that mapping is authoritative — a group that is missing
 * or explicitly set to `[]` gets zero routes, rather than inheriting the global
 * union. Only legacy competitions without any per-group configuration fall back
 * to the global `settings.routeIds`.
 */
export function getGroupRouteIds(settings, groupId) {
  const gri = settings?.groupRouteIds;
  const perGroupActive = gri && typeof gri === "object" && Object.keys(gri).length > 0;
  if (perGroupActive && groupId) {
    return gri[groupId] || [];
  }
  return settings?.routeIds || [];
}

/**
 * Route IDs that belong on a competitor's scorecard for `groupId`:
 * the group's qualification routes followed by that group's finals routes
 * (deduped, finals after quali). Finals routes are printed for every
 * competitor of a group that has finals — finalists aren't known at print
 * time, and pre-printing them means no reprinting once finals are seeded.
 * Returns { qualiIds, finalsIds } (finalsIds already excludes any id
 * already in qualiIds) so the caller can render a section divider.
 */
export function getScorecardRouteIds(settings, groupId, finalsRouteIdsByGroup) {
  const qualiIds = getGroupRouteIds(settings, groupId) || [];
  const qualiSet = new Set(qualiIds);
  const rawFinals = (finalsRouteIdsByGroup && finalsRouteIdsByGroup[groupId]) || [];
  const seen = new Set();
  const finalsIds = [];
  for (const id of rawFinals) {
    if (!id || qualiSet.has(id) || seen.has(id)) continue;
    seen.add(id);
    finalsIds.push(id);
  }
  return { qualiIds, finalsIds };
}

/**
 * Groups a BLANK scorecard stack can be printed for — the walk-in sheets the
 * desk keeps ready: no name, no session, just one group's routes, so whoever
 * turns up mid-session gets a sheet with their name written on it by hand.
 *
 * A group whose scorecard route set resolves to nothing would print an empty
 * table, which is worse than no sheet at all — those are dropped here, in ONE
 * place, so the dialog's "N sheets" preview and the PDF can't disagree.
 *
 * @param {Object} settings          - competition `settings` (route assignment)
 * @param {Array}  groups            - the competition's groups
 * @param {Array}  sessions          - the competition's sessions (for finals)
 * @param {Iterable} availableRouteIds - route ids that actually resolve to a route
 * @param {string|null} groupId      - restrict to one group (null = every group)
 * @returns {Array} groups in alphabetical order; `[null]` for a competition
 *                  with no groups at all (one stack off the global route list);
 *                  `[]` when nothing printable remains.
 */
export function getBlankScorecardGroups(settings, groups, sessions, availableRouteIds, groupId = null) {
  const available = availableRouteIds instanceof Set ? availableRouteIds : new Set(availableRouteIds || []);
  const finalsByGroup = {};
  getFinalsGroupsInfo(groups || [], sessions || []).forEach(fi => {
    finalsByGroup[fi.groupId] = fi.finalsRouteIds || [];
  });
  const candidates = groupId
    ? (groups || []).filter(g => g.id === groupId)
    : ((groups || []).length ? sortGroupsForDisplay(groups) : [null]);
  return candidates.filter(g => {
    const { qualiIds, finalsIds } = getScorecardRouteIds(settings, g?.id || null, finalsByGroup);
    return qualiIds.some(id => available.has(id)) || finalsIds.some(id => available.has(id));
  });
}

/**
 * Returns the union of every routeId referenced by a competition: the legacy
 * `settings.routeIds` plus the values of every per-group entry in
 * `settings.groupRouteIds`. Used by close-comp archiving and by stats filters
 * that need to exclude all routes belonging to active competitions.
 *
 * @param {Object} comp - Competition document
 * @returns {string[]} Deduplicated route IDs (order not guaranteed)
 */
export function getAllCompRouteIds(comp) {
  const ids = new Set();
  (comp?.settings?.routeIds || []).forEach(id => { if (id) ids.add(id); });
  const gri = comp?.settings?.groupRouteIds;
  if (gri && typeof gri === "object") {
    for (const arr of Object.values(gri)) {
      (arr || []).forEach(id => { if (id) ids.add(id); });
    }
  }
  return [...ids];
}

/**
 * Returns the Set of routeIds that belong to currently-running, non-external
 * competitions. Useful for stats / occupancy / map filters that need to hide
 * those routes from gym-wide views (they're tracked separately in the comp's
 * own `attempts` subcollection, not in `gyms/{gymId}/ascents`).
 *
 * @param {Array<Object>} competitions
 * @returns {Set<string>}
 */
export function getActiveCompRouteIdSet(competitions) {
  const ids = new Set();
  for (const c of competitions || []) {
    if (c.status !== "active" || c.type === "external") continue;
    for (const id of getAllCompRouteIds(c)) ids.add(id);
  }
  return ids;
}

/**
 * What a route is worth **in this competition** — or `null` when the scoring
 * system gives routes no point value at all.
 *
 * The gym's own difficulty points (`difficulty[route.difficulty].points`) are
 * NEVER the answer here. They describe what the route pays into the gym's
 * lifetime score; a competition either assigns its own value or ranks on tops
 * and zones alone. Printing the gym number on a competition card was actively
 * misleading — it looked like the competitor was chasing it.
 *
 *   "classic_redpoint" — fixed per-route value the organizer set, per group;
 *                        0 where none was set, exactly the way
 *                        calculateClassicRedpointRanking() scores it, so the
 *                        card and the ranking always agree. There is NO
 *                        fallback to a stored route value: the route doc
 *                        carries no point field any more (it was written once
 *                        at creation and never maintained).
 *   "points"           — the pool split among everyone in the group who has
 *                        topped the route so far, i.e. what one more top is
 *                        worth RIGHT NOW. Drops as the route gets climbed.
 *                        With nobody up yet — or with no group to split
 *                        inside — the whole pool is on offer.
 *   everything else    — null (tops/zones ranking; a route has no price).
 *
 *   settings  — competition.settings
 *   route     — route doc (only `id` is read)
 *   opts.groupId       — the group whose scoring applies (per-group redpoint
 *                        values; pool share is counted within the group)
 *   opts.attempts      — attempts to count toppers from ("points" only)
 *   opts.registrations — registrations, so an unpaid competitor does not
 *                        dilute the pool here while the ranking ignores them
 */
export function getCompetitionRoutePoints(settings, route, opts = {}) {
  if (!route) return null;
  const { groupId = null, attempts = null, registrations = null } = opts;
  const system = settings?.scoringSystem || "tops_and_zones";

  if (system === "classic_redpoint") {
    return getGroupRouteCompPoints(settings, groupId)[route.id] ?? 0;
  }

  if (system === "points") {
    const pool = settings?.pointPool || 1000;
    // The split is only knowable inside a group — the pool is divided among
    // that group's toppers, and a judge's route list spans every group at
    // once. With no group in hand, show the undivided pool rather than a
    // cross-group number that nobody is actually competing for.
    if (!groupId || !attempts) return pool;
    const scoped = filterPaidCompetitors(
      attempts.filter(a => a.routeId === route.id && a.topped && a.groupId === groupId),
      registrations || [],
      settings?.entryFee?.enabled === true
    );
    // Same rounding as calculatePointsRanking so the card never shows a share
    // that the leaderboard then contradicts by a decimal.
    return scoped.length > 0 ? Math.round((pool / scoped.length) * 10) / 10 : pool;
  }

  return null;
}
